import { useCallback, useRef, useState, useEffect } from "react";
import RecordRTC from "recordrtc";
import axios from "axios";
import { setIsRecording, setRecordFaild, setRecordFade } from '../store/chat/chat-slice';
import { AppDispatch } from '../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { getIsRecording } from '../store/chat/chat-selectors';

type LongPressOptions = {
  shouldPreventDefault?: boolean;
  delay?: number;
};

const useLongPress = (
  onLongPress: (event: MouseEvent | TouchEvent) => void,
  onClick: () => void,
  { shouldPreventDefault = true, delay = 500 }: LongPressOptions = {},
  handleSendRecord: (text: string) => void
) => {
  const dispatch: AppDispatch = useDispatch();
  const [longPressTriggered, setLongPressTriggered] = useState<boolean>(false);
  const [recorder, setRecorder] = useState<RecordRTC | null>(null);
  const isRecording = useSelector(getIsRecording);
  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const apiUrlRec = process.env.REACT_APP_API_URL_REC;

  const [startX, setStartX] = useState<number | null>(null);
  const [buttonPosition, setButtonPosition] = useState<number>(0);
  const [opacity, setOpacity] = useState<number>(1);
  const isDragging = useRef<boolean>(false);
  const recordingInterval = useRef<NodeJS.Timeout | null>(null);
  const [recordingTime, setRecordingTime] = useState<number>(0);
  const clickDuration = useRef<number | null>(null);
  const [isCanceled, setIsCanceled] = useState<boolean>(false);

  const [hasAccess, setHasAccess] = useState(false);

  const requestMicroAccess = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      setHasAccess(true);
      return true;
    } catch (err) {
      console.error("Доступ к микрофону запрещён", err);
      setHasAccess(false);
      return false;
    }
  };

  const handleRecord = useCallback(async () => {
    if (isRecording && recorder) {
      recorder.stopRecording(async () => {
        const audioBlob = recorder.getBlob();
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64Audio = reader.result?.toString().split(',')[1];
          try {
            const response = await axios.post(apiUrlRec || '', { audio: base64Audio });
            const text = response.data.text;

            if (text && text.trim() !== '') {
              handleSendRecord(text);
              dispatch(setRecordFaild(false));
            } else {
              dispatch(setRecordFaild(true));
              dispatch(setRecordFade(false));
              setTimeout(() => {
                dispatch(setRecordFade(true));
                setTimeout(() => dispatch(setRecordFaild(false)), 500);
              }, 1000);
            }
          } catch (error) {
            dispatch(setRecordFaild(true));
            dispatch(setRecordFade(false));
            setTimeout(() => {
              dispatch(setRecordFade(true));
              setTimeout(() => dispatch(setRecordFaild(false)), 500);
            }, 1000);
          }

          // Сброс таймера записи после отправки
          setRecordingTime(0);
        };
        reader.readAsDataURL(audioBlob);
      });
      dispatch(setIsRecording(false));
      if (recordingInterval.current) {
        clearInterval(recordingInterval.current);
        recordingInterval.current = null;
      }
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const newRecorder = new RecordRTC(stream, { type: 'audio', disableLogs: true });
        newRecorder.startRecording();
        setRecorder(newRecorder);
        dispatch(setIsRecording(true));

        recordingInterval.current = setInterval(() => {
          setRecordingTime((prevTime) => prevTime + 100);
        }, 100);



      } catch (error) {
        dispatch(setIsRecording(false));
      }
    }
  }, [isRecording, recorder, apiUrlRec, dispatch, handleSendRecord]);

  useEffect(() => {
    if (isRecording) {
      // Через 5 секунд "отпускаем" кнопку
      const stopTimeout = setTimeout(() => {
        const mouseUpEvent = new MouseEvent('mouseup', {
          bubbles: true,
          cancelable: true,
        });
        document.dispatchEvent(mouseUpEvent);  // Симулируем событие mouseup
        clear();  // Останавливаем запись (если нужно)
      }, 30000);

      // Очистка таймера при изменении isRecording
      return () => clearTimeout(stopTimeout);
    }
  }, [isRecording]);


  const start =  useCallback(
    async (event: React.MouseEvent | React.TouchEvent) => {
      clickDuration.current = Date.now();

      if (shouldPreventDefault && event.currentTarget) {
        (event.currentTarget as HTMLElement).addEventListener("touchend", preventDefault, { passive: false });
      }

      const startPosX = 'touches' in event ? event.touches[0].clientX : event.clientX;
      setStartX(startPosX);
      isDragging.current = true;

      const ok = hasAccess || (await requestMicroAccess());
      if (!ok) return;

      timeout.current = setTimeout(() => {
        onLongPress(event as any);
        setLongPressTriggered(true);
        handleRecord();
      }, delay);
    },
    [onLongPress, delay, shouldPreventDefault, handleRecord]
  );

  const move = useCallback(
    (event: MouseEvent) => {
      if (isDragging.current && startX !== null && isRecording) {
        const currentPosX = event.clientX;
        const distance = currentPosX - startX;
        const sensitivityMultiplier = 1.7;
        const isMobile = window.innerWidth <= 1024;
        const limitDistance = isMobile ? 150 : 350;
        const limitedDistance = Math.max(Math.min(distance * sensitivityMultiplier, 0), -limitDistance);

        setButtonPosition(limitedDistance);
        const textOpacity = Math.max(1 - Math.abs(limitedDistance) / (limitDistance / 2), 0);
        setOpacity(textOpacity);

        const thresholdDistance = -limitDistance * 0.95;
        if (limitedDistance < thresholdDistance) {
          setIsCanceled(true);
          dispatch(setIsRecording(false));
          resetRecording();
        }
      }
    },
    [startX, isRecording, dispatch]
  );

  const clear = useCallback(() => {
    setOpacity(1);
    setButtonPosition(0);
    isDragging.current = false;

    if (isCanceled) {
      setIsCanceled(false);
      resetRecording();
    } else {
      if (longPressTriggered) {
        dispatch(setIsRecording(false));
        handleRecord();
      } else {
        onClick();
      }
    }

    setLongPressTriggered(false);
    setStartX(null);

    if (timeout.current) {
      clearTimeout(timeout.current);
      timeout.current = undefined;
    }
  }, [dispatch, handleRecord, isCanceled, longPressTriggered, onClick]);

  const resetRecording = () => {
    if (recordingInterval.current) {
      clearInterval(recordingInterval.current);
      recordingInterval.current = null;
      setRecordingTime(0);
    }
    if (recorder) {
      recorder.stopRecording();
      setRecorder(null);
    }
  };


  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      move(event);
    };

    const handleMouseUp = () => {
      if (isDragging.current) {
        clear();
      }
    };

    const handleTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      const simulatedMouseEvent = new MouseEvent("mousemove", {
        clientX: touch.clientX,
        clientY: touch.clientY,
        bubbles: true,
      });
      move(simulatedMouseEvent);
    };

    const handleTouchEnd = () => {
      if (isDragging.current) {
        clear();
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [move, clear]);

  const buttonColor = isCanceled ? '#CC3939' : buttonPosition < 0 ? '#CC3939' : '#7856c1';
  const boxShadow = isRecording ? isCanceled ? 'none' : buttonPosition < 0 ? 'none' : '0 0 0 4px rgba(120, 86, 193, 0.5)' : 'none';
  const animation = isRecording && isCanceled ? 'none' : buttonPosition < 0 ? 'none' : 'pulse 1s infinite';

  const textStyle = {
    transform: `translateX(${buttonPosition}px)`,
    opacity: opacity, // Используйте opacity, чтобы контролировать прозрачность текста
    color: isCanceled || buttonPosition < 0 ? '#CC3939' : 'inherit', // Изменение цвета текста на красный
    transition: 'transform 0.2s ease, opacity 0.2s ease, color 0.2s ease',
  };

  return {
    onMouseDown: (e: React.MouseEvent) => start(e),
    onTouchStart: (e: React.TouchEvent) => start(e),
    buttonStyle: {
      transform: `translateX(${buttonPosition}px)`,
      opacity: opacity,
      backgroundColor: buttonColor,
      boxShadow: boxShadow,
      animation: isRecording && !(buttonPosition < 0) ? 'pulse 1s infinite' : 'none',
      transition: 'transform 0.2s ease, opacity 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease',
    },
    textStyle, // Добавьте здесь textStyle
    recordingTime,
    formattedRecordingTime: formatTime(recordingTime),
  };
};

const formatTime = (milliseconds: number) => {
  const seconds = Math.floor((milliseconds / 1000) % 60);
  const minutes = Math.floor((milliseconds / (1000 * 60)) % 60);
  return `${String(seconds).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const isTouchEvent = (event: Event): event is TouchEvent => {
  return "touches" in event;
};

const preventDefault = (event: Event) => {
  if (!isTouchEvent(event)) return;

  if (event.touches.length < 2 && event.preventDefault) {
    event.preventDefault();
  }
};

export default useLongPress;
